import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Grid,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Box,
  Divider,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../../services/api_service";
import { GetPriceHistoryAPI, stockTransferDataAPI } from "../../../config";
import Layout from "../../../layout/Layout";
import { dateColumnFilterProps } from "../../../components/MrtDateColumnFilter";
import { formatNumber, formatAED } from "../../../utils/format";
import moment from "moment";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";
import { max } from "date-fns";

const ProductDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const productState = location.state || {};

  const { product_id, store_id, store_name, product_name, sku, in_stock, uom } =
    productState;

  const [stockDetails, setStockDetails] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [currentPrice, setCurrentPrice] = useState({
    buying_price: 0,
    selling_price: 0,
  });
  const [loading, setLoading] = useState(true);
  const formatPrice = (price) => formatAED(price);

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);

      // 🔹 Fetch stock details
      const stockResponse = await apiData(
        "get",
        `${stockTransferDataAPI}?product_id=${product_id}&store_id=${store_id}`
      );
      if (stockResponse.success) setStockDetails(stockResponse.data || []);

      // 🔹 Fetch price history
      const query = new URLSearchParams({ product_id, store_id }).toString();
      const priceResponse = await apiData(
        "get",
        `${GetPriceHistoryAPI}?${query}`
      );

      if (priceResponse.success) {
        const data = priceResponse.data || [];

        // ✅ pick latest price history entry by ID
        const latestPrice =
          data.length > 0
            ? data.reduce((max, item) => (item.id > max.id ? item : max))
            : null;

        // ✅ pick the latest stock price entry
        const lastStockPrice =
          stockResponse.data && stockResponse.data.length > 0
            ? stockResponse.data[stockResponse.data.length - 1]
            : null;

        setCurrentPrice({
          buying_price: latestPrice?.buying_price || lastStockPrice?.buying_price || 0,
          selling_price: latestPrice?.selling_price || lastStockPrice?.selling_price || 0,
        });

        setPriceHistory(data);
      }

    } catch (error) {
      console.error("Failed to fetch product details:", error);
      setStockDetails([]);
      setPriceHistory([]);
      setCurrentPrice({
        buying_price: 0,
        selling_price: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (product_id && store_id) fetchData();
  }, [product_id, store_id]);

  // Export both tables shown on this page into one workbook (Batch Stock +
  // Price History sheets). Both tables load all rows, so this covers everything.
  const handleExportData = () => {
    if (!stockDetails.length && !priceHistory.length) {
      alert("No data to export!");
      return;
    }
    const fDate = (v) => (v ? moment(v).format("DD-MM-YYYY") : "");
    const batch = stockDetails.map((r, i) => ({
      "#": i + 1,
      "Manufacture Date": fDate(r.manufacture_date),
      "Life Days": r.life_days ?? "",
      "Available Quantity": Number(r.stock_count) || 0,
      "Buying Price (AED)": Number(r.buying_price) || 0,
      "Selling Price (AED)": Number(r.selling_price) || 0,
    }));
    const hist = priceHistory.map((r, i) => ({
      "#": i + 1,
      "Last Updated On": fDate(r.created_at),
      "Buying Price (AED)": Number(r.buying_price) || 0,
      "Selling Price (AED)": Number(r.selling_price) || 0,
      "Manufacture Date": fDate(r.manufacture_date),
      "Life Days": r.life_days ?? "",
    }));
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(
      batch.length ? batch : [{ "Manufacture Date": "No batches" }]
    );
    fitColumns(ws1);
    XLSX.utils.book_append_sheet(wb, ws1, "Batch Stock");
    const ws2 = XLSX.utils.json_to_sheet(
      hist.length ? hist : [{ "Last Updated On": "No history" }]
    );
    fitColumns(ws2);
    XLSX.utils.book_append_sheet(wb, ws2, "Price History");
    const safe = (s) => String(s || "product").replace(/[^\w.-]+/g, "_");
    writeXlsxFrozen(
      XLSX,
      wb,
      `product_details_${safe(sku || product_name)}_${moment().format("DD_MM_YYYY")}.xlsx`
    );
  };

  if (!product_id || !store_id) {
    return (
      <Layout>
        <Typography sx={{ p: 3 }}>No product selected.</Typography>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 5 }}
        >
          <Grid item>
            <Typography variant="h5" fontWeight={600}>
              Product Details
            </Typography>

            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
              {/* <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link> */}
              <Link
                underline="hover"
                color="inherit"
                href="/reports/stock-report"
              >
                Stock Inventory Report
              </Link>
              <Link
                underline="hover"
                color="inherit"
                href={`/reports/stock-report/${store_id}`}
              >
                {store_name}
              </Link>
              <Typography color="text.primary">Product Details</Typography>
            </Breadcrumbs>
          </Grid>

          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <Button
              variant="contained"
              color="success"
              onClick={handleExportData}
              startIcon={<FileDownloadIcon />}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography
          variant="h6"
          ml={1}
          mb={2}
          fontWeight={600}
          display="flex"
          textAlign="left"
          gutterBottom
        >
          {product_name} - {sku} ( {store_name} )
        </Typography>
        {/* Product Info */}
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            {/* <Typography variant="h6" fontWeight={600} gutterBottom>
              {product_name} - {store_name}
            </Typography> */}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Current Stock
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {in_stock ?? "N/A"} {uom || ""}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Current Buying Price
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatPrice(currentPrice.buying_price)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Current Selling Price
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatPrice(currentPrice.selling_price)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stock Overview */}
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Current Stock Overview (Batch-wise)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Summary of all batches currently available in the store.
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <MaterialReactTable
                enableGlobalFilter={false}
                enablestickHeader
                enableFullScreenToggle={false}
                columns={[
                  {
                    accessorKey: "manufacture_date",
                    header: "Manufacture Date",
                    ...dateColumnFilterProps(),
                    Cell: ({ row }) =>
                      moment(row.original.manufacture_date).format(
                        "DD-MM-YYYY"
                      ),
                  },
                  { accessorKey: "life_days", header: "Life Days" },
                  {
                    accessorKey: "stock_count",
                    header: "Available Quantity",
                    Cell: ({ cell }) => formatNumber(cell.getValue()),
                  },
                  {
                    accessorKey: "buying_price",
                    header: "Buying Price (AED)",
                    Cell: ({ cell }) =>
                      `${formatPrice(cell.getValue())}`,
                  },
                  {
                    accessorKey: "selling_price",
                    header: "Selling Price (AED)",
                    Cell: ({ cell }) =>
                      `${formatPrice(cell.getValue())}`,
                  },
                ]}
                data={stockDetails}
                enablePagination={false}
                enableSorting={false}
                enableColumnFilters={false}
                muiTableContainerProps={{ sx: { maxHeight: "50vh" } }}
              />
            )}
          </CardContent>
        </Card>

        {/* Price History */}
        <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Price Change History
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <MaterialReactTable
                enableGlobalFilter={false}
                enablestickHeader
                enableFullScreenToggle={false}
                columns={[
                  {
                    accessorKey: "created_at",
                    header: "Last Updated On",
                    ...dateColumnFilterProps(),
                    Cell: ({ row }) =>
                      moment(row.original.created_at).format("DD-MM-YYYY"),
                  },
                  {
                    accessorKey: "buying_price",
                    header: "Buying Price (AED)",
                    Cell: ({ cell }) =>
                      `${formatPrice(cell.getValue())}`,
                  },
                  {
                    accessorKey: "selling_price",
                    header: "Selling Price (AED)",
                    Cell: ({ cell }) =>
                      `${formatPrice(cell.getValue())}`,
                  },
                  {
                    accessorKey: "manufacture_date",
                    header: "Manufacture Date",
                    Cell: ({ cell }) =>
                      cell.getValue() ? moment(cell.getValue()).format("DD-MM-YYYY") : "-",
                    ...dateColumnFilterProps(),
                  },
                  { accessorKey: "life_days", header: "Life Days" },
                ]}
                data={priceHistory}
                enablePagination={false}
                enableSorting={false}
                enableColumnFilters={false}
                muiTableContainerProps={{ sx: { maxHeight: "50vh" } }}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default ProductDetails;
